import { DialogActions, DialogTitle, useTheme } from "@mui/material";
import { useCreateProviderSearchAccount } from "apollo/hooks/mutations";
import { useProviderSearchContext } from "context/ProviderSearchContext";
import {
  DOCUMENT_PRIVACY_POLICY,
  DOCUMENT_TERMS_AND_CONDITIONS,
  TRACK_EVENTS,
} from "core/consts";
import { getStaticAsset } from "core/model/config";
import { getApolloNetworkErrorMessage } from "core/model/utils/errors";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import LinkV2, { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import RSButton from "ds_legacy/components/RSButton";
import TextInputField from "ds_legacy/components/TextInputField";
import { HorizontalDivider, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import { Caption, FONT_SIZE_12 } from "ds_legacy/materials/typography";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { AsteriskExplained } from "dsl/molecules/AsteriskExplained";
import { useState } from "react";
import {
  SimpleFormRenderProps,
  composeValidation,
  convertModelDefinition,
  validateModel,
  valueDef,
} from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { getDialogProps } from ".";
import {
  LoginErrorType,
  LoginErrors,
  SIGNUP_ERROR_TYPE_ALREADY_EXISTS,
} from "./Error";
import { GoToModal } from "./GoToModule";
import {
  CaptionWithLink,
  DialogContentWrapper,
  PROVIDER_SEARCH_LOGIN_MODAL,
  PROVIDER_SEARCH_SIGNUP_MODAL_SUCCESS,
  ProviderRegistrationInfo,
  ProviderSearchDialog,
  providerSearchEmailDef,
} from "./shared";

type FormInput = {
  confirm_email: string;
  email: string;
  privacy_policy: boolean;
  terms_and_conditions: boolean;
};

const defaultValue: FormInput = {
  confirm_email: "",
  email: "",
  privacy_policy: false,
  terms_and_conditions: false,
};

const emailsMatch = ({
  confirm_email,
  email,
}: {
  confirm_email: string;
  email: string;
}) => {
  const sanitizedEmail = email.toLowerCase().trim();
  const sanitizedConfirmEmail = confirm_email.toLowerCase().trim();
  if (!sanitizedEmail || !sanitizedConfirmEmail) {
    return true;
  }

  if (sanitizedEmail !== sanitizedConfirmEmail) {
    return false;
  }

  return true;
};

const modelDefinition = convertModelDefinition({
  ...providerSearchEmailDef("email"),
  ...providerSearchEmailDef("confirm_email"),
  ...valueDef("terms_and_conditions", {
    validate(value, { translations }) {
      return value
        ? true
        : {
            customMessage:
              translations.providersearch.loginModal.createAccount
                .acceptTermsError,
          };
    },
  }),
  ...valueDef("privacy_policy", {
    validate(value, { translations }) {
      return value
        ? true
        : {
            customMessage:
              translations.providersearch.loginModal.createAccount
                .acceptDPAError,
          };
    },
  }),
});

const validate =
  (translations: Translations) => (formInput: FormInput | null) => {
    const { confirm_email, email } = formInput || defaultValue;
    if (
      !emailsMatch({
        confirm_email,
        email,
      })
    ) {
      return {
        confirm_email: {
          customMessage:
            translations.providersearch.loginModal.createAccount
              .emailErrorNotMatch,
        },
      };
    }

    return true;
  };

const STEP_BY_STEP_GUIDE = "Anleitung_nutzerkonto_pflegefinder.pdf";

export const CreateAccountModal = ({
  onClose,
  setAccountEmail,
}: {
  onClose: () => void;
  setAccountEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  const { loginModal, setLoginModal } = useProviderSearchContext();
  const { isMobile } = useMedia();
  const props = getDialogProps({ isMobile, loginModal });
  const { getDocumentUrl } = useLegalDocuments();
  const theme = useTheme();
  const { isDesktop } = useMedia();
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const { trackEvent } = useTracking();
  const [error, setError] = useState<LoginErrorType>();
  const [createAccount, createAccountProgress] = useCreateProviderSearchAccount(
    {
      onCompleted: () => setLoginModal(PROVIDER_SEARCH_SIGNUP_MODAL_SUCCESS),
      onError: (err) => {
        if (
          getApolloNetworkErrorMessage(err)?.includes("account already exists")
        ) {
          setError(SIGNUP_ERROR_TYPE_ALREADY_EXISTS);
          return;
        }
        console.error(`provider search - unhandled account create error`, {
          err,
        });
      },
    },
  );

  return (
    <ProviderSearchDialog paperStyle={props.paperStyle} onClose={onClose}>
      <DialogContentWrapper testId="create-account-modal">
        <SimpleFormRenderProps
          asHtmlForm
          formInputValue={defaultValue}
          modelDefinition={modelDefinition}
          validate={composeValidation(
            validate(translations),
            validateModel(modelDefinition),
          )}
          onSubmit={({ email }: FormInput) => {
            const sanitizedEmail = email.toLowerCase().trim();
            createAccount({ email: sanitizedEmail });
            setAccountEmail(sanitizedEmail);
          }}
        >
          {({ submit }) => {
            return (
              <>
                <DialogTitle
                  sx={{ paddingBottom: 0, paddingTop: 0 }}
                  textAlign="center"
                >
                  {translations.providersearch.loginModal.createAccount.title}
                </DialogTitle>
                <LinkV2
                  aria-label={externalLinkLabel(
                    translations.providersearch.loginModal.createAccount
                      .accountCreationGuide,
                    "pdf",
                  )}
                  href={getStaticAsset(STEP_BY_STEP_GUIDE)}
                  style={{ fontSize: FONT_SIZE_12 }}
                  target="_blank"
                  onClick={() =>
                    trackEvent({
                      name: TRACK_EVENTS.PROVIDER_SEARCH_ACCOUNT_CREATION_GUIDE_CLICKED,
                    })
                  }
                >
                  {
                    translations.providersearch.loginModal.createAccount
                      .accountCreationGuide
                  }
                </LinkV2>
                <VerticalLayout
                  aligned
                  style={{
                    boxSizing: "border-box",
                    maxWidth: dp(328),
                    overflow: "visible",
                    padding: padding(1, 1, 0, 1),
                    width: isMobile ? "95%" : dp(328),
                  }}
                >
                  <AsteriskExplained />
                  <TextInputField
                    elementName="email"
                    fullWidth
                    hasCustomValidation
                    label={
                      translations.providersearch.loginModal.createAccount
                        .emailOnePlaceholder
                    }
                    marginOverride={margin(1, 0, 1)}
                    required
                    textInputType="email"
                    variant="outlined"
                  />
                  <TextInputField
                    elementName="confirm_email"
                    fullWidth
                    hasCustomValidation
                    label={
                      translations.providersearch.loginModal.createAccount
                        .confirmEmailPlaceholder
                    }
                    marginOverride={margin(1, 0, 2)}
                    required
                    textInputType="email"
                    variant="outlined"
                  />
                  <HorizontalDivider
                    color={theme.palette.neutrals.medium_grey}
                  />
                  <VerticalLayout
                    style={{
                      alignItems: "flex-start",
                      overflow: "visible",
                      width: "100%",
                    }}
                  >
                    <CheckboxInputField
                      elementName="terms_and_conditions"
                      label={
                        <CaptionWithLink
                          href={getDocumentUrl(DOCUMENT_TERMS_AND_CONDITIONS)}
                          message={
                            translations.providersearch.loginModal.createAccount
                              .acceptTerms
                          }
                          type="pdf"
                        />
                      }
                      required
                    />
                    <CheckboxInputField
                      elementName="privacy_policy"
                      label={
                        <CaptionWithLink
                          href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
                          message={
                            translations.providersearch.loginModal.createAccount
                              .acceptDPA
                          }
                          type="pdf"
                        />
                      }
                      required
                    />
                    <Caption
                      as="p"
                      fontSize={FONT_SIZE_12}
                      margin={margin(2, 0)}
                      whiteSpace="normal"
                    >
                      {
                        translations.providersearch.loginModal.createAccount
                          .infoText
                      }
                    </Caption>
                  </VerticalLayout>
                </VerticalLayout>
                <DialogActions
                  sx={{
                    boxSizing: "border-box",
                    flexDirection: "column",
                    maxWidth: dp(328),
                    padding: padding(0, 1),
                    width: "100%",
                  }}
                >
                  <RSButton
                    buttonWrapperStyle={{
                      width: "100%",
                    }}
                    color="primary"
                    id="sign-up-submit"
                    loading={createAccountProgress}
                    style={{ margin: margin(1, 0.1), width: "100%" }}
                    type="submit"
                    variant="contained"
                    onClick={submit}
                  >
                    {
                      translations.providersearch.loginModal.createAccount
                        .submitButton
                    }
                  </RSButton>
                </DialogActions>
                <LoginErrors error={error} />
                <GoToModal
                  goToModal={PROVIDER_SEARCH_LOGIN_MODAL}
                  translation={
                    translations.providersearch.loginModal.createAccount
                      .logInLinkInfo
                  }
                />
              </>
            );
          }}
        </SimpleFormRenderProps>
        {!isDesktop ? (
          <>
            <HorizontalDivider color={theme.palette.neutrals.medium_grey} />
            <ProviderRegistrationInfo aligned />
          </>
        ) : null}
      </DialogContentWrapper>
    </ProviderSearchDialog>
  );
};
